import { Injectable } from '@angular/core';
import { DATE_FORMAT_DAYJS } from '@shared/constants';
import {
    API_DATE_FORMAT,
    ICalendarData,
    IReportTimeAdvancedConfigPoint,
    IReportTimeConfig,
    IReportTimeConfigPoint,
    IReportTimeStandardConfig,
    MonthOfTheYearLabel,
    ReportTimeConfigPointPosition,
    TemplateTimeType,
    TimeConfigType,
    TimeConfigYearType,
    TimePeriodConfigType,
} from '@shared/models';
import dayjs from 'dayjs';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { isNil } from 'lodash-es';
import { Observable, catchError, map, of } from 'rxjs';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root',
})
export class TimePeriodService {
    constructor(private apiService: ApiService) {
        dayjs.extend(weekOfYear);
        dayjs.extend(isLeapYear);
        dayjs.extend(isoWeeksInYear);
    }

    getSimpleLabelForAdvancedTimeConfig(
        timeConfigPoint: IReportTimeAdvancedConfigPoint,
        position: ReportTimeConfigPointPosition,
        timeType?: TemplateTimeType | null,
    ): string {
        if (!timeConfigPoint || !position) {
            return '';
        }
        switch (timeConfigPoint.type) {
            case TimeConfigType.TODAY: {
                return 'Today (when report is run)';
            }
            case TimeConfigType.DATE: {
                return dayjs(timeConfigPoint.date).format(DATE_FORMAT_DAYJS);
            }
            case TimeConfigType.DAYS_BEFORE: {
                return timeType === TemplateTimeType.FUTURE
                    ? `${timeConfigPoint.before ?? 0} days after report is run`
                    : `${timeConfigPoint.before ?? 0} days before report is run`;
            }
            case TimeConfigType.CALENDAR_WEEKS_BEFORE: {
                return timeType === TemplateTimeType.FUTURE
                    ? `${timeConfigPoint.before ?? 0} calendar weeks after report is run`
                    : `${timeConfigPoint.before ?? 0} calendar weeks before report is run`;
            }
            case TimeConfigType.ASDA_WEEKS_BEFORE: {
                return timeType === TemplateTimeType.FUTURE
                    ? `${timeConfigPoint.before ?? 0} Asda weeks after report is run`
                    : `${timeConfigPoint.before ?? 0} Asda weeks before report is run`;
            }
            case TimeConfigType.MONTH: {
                return `${MonthOfTheYearLabel.get(timeConfigPoint.month!) ?? 0}` + `${MonthOfTheYearLabel.get(timeConfigPoint.year!) ?? 0}`;
            }
            case TimeConfigType.MONTHS_BEFORE: {
                return timeType === TemplateTimeType.FUTURE
                    ? `${timeConfigPoint.before ?? 0} months after report is run`
                    : `${timeConfigPoint.before ?? 0} months before report is run`;
            }
            case TimeConfigType.WALMART_WEEK: {
                return `Walmart Week #${timeConfigPoint.walmartWeek ?? 0}`;
            }
            case TimeConfigType.ASDA_WEEK: {
                // const year = dayjs().year() - timeConfigPoint.yearsBefore!
                return `Asda Week #${timeConfigPoint.asdaWeek}`;
            }
            case TimeConfigType.WHOLE_YEAR: {
                const dateValue =
                    timeType === TemplateTimeType.FUTURE
                        ? dayjs().add(timeConfigPoint.yearsBefore ?? 0, 'years')
                        : dayjs().subtract(timeConfigPoint.yearsBefore ?? 0, 'years');
                if (position === ReportTimeConfigPointPosition.START) {
                    if (timeConfigPoint.yearType === TimeConfigYearType.CALENDAR) {
                        return `Calendar year - ${dateValue.startOf('year').format(DATE_FORMAT_DAYJS)}`;
                    } else if (timeConfigPoint.yearType === TimeConfigYearType.FISCAL) {
                        return `Fiscal year - ${dateValue.startOf('year').add(1, 'month').format(DATE_FORMAT_DAYJS)}`;
                    }
                } else if (position === ReportTimeConfigPointPosition.END) {
                    if (timeConfigPoint.yearType === TimeConfigYearType.CALENDAR) {
                        return `Calendar year - ${dateValue.endOf('year').format(DATE_FORMAT_DAYJS)}`;
                    } else if (timeConfigPoint.yearType === TimeConfigYearType.FISCAL) {
                        return `Fiscal year - ${dateValue.endOf('year').add(1, 'month').format(DATE_FORMAT_DAYJS)}`;
                    }
                }
                const year =
                    timeType === TemplateTimeType.FUTURE ? dayjs().year() + timeConfigPoint.yearsBefore! : dayjs().year() - timeConfigPoint.yearsBefore!;
                if (position === ReportTimeConfigPointPosition.START) {
                    if (timeConfigPoint.yearType === TimeConfigYearType.CALENDAR) {
                        return `January 1, ${year ?? 0}`;
                    } else if (timeConfigPoint.yearType === TimeConfigYearType.FISCAL) {
                        return `February 1, ${year ?? 0}`;
                    }
                } else if (position === ReportTimeConfigPointPosition.END) {
                    if (timeConfigPoint.yearType === TimeConfigYearType.CALENDAR) {
                        return `December 31, ${year ?? 0}`;
                    } else if (timeConfigPoint.yearType === TimeConfigYearType.FISCAL) {
                        return `January 31, ${year ?? 0}`;
                    }
                }
                return '';
            }
            case TimeConfigType.CURRENT_WEEK: {
                return `${timeConfigPoint.type}`;
            }
            case TimeConfigType.ASDA_MONTH: {
                return `${timeConfigPoint.asdaMonth} - ${timeConfigPoint.year}`;
            }
            case TimeConfigType.ASDA_YEAR: {
                return `${timeConfigPoint.AsdaFiscalYearToDate} - ${timeConfigPoint.yearType} `;
            }
            case TimeConfigType.FIXED_DATE: {
                return `${timeConfigPoint.date} `;
            }
            case TimeConfigType.LAST_WEEK: {
                return `${timeConfigPoint.before} `;
            }
            case TimeConfigType.LAST_YEAR_CURRENT_WEEK: {
                return `${timeConfigPoint.yearsBefore} `;
            }
            case TimeConfigType.LAST_YEAR_LAST_WEEK: {
                return `Before ${timeConfigPoint.yearsBefore} years -  ${timeConfigPoint.before} weeks`;
            }
            case TimeConfigType.LAST_YEAR_WEEKS_BEFORE: {
                return `Before ${timeConfigPoint.yearsBefore} years -  ${timeConfigPoint.before} weeks`;
            }
            case TimeConfigType.ASDA_WEEKS_BEFORE: {
                return `Before ${timeConfigPoint.before}`;
            }
            default: {
                return 'Unknown time period type';
            }
        }
    }

    getSimpleLabel(timeConfig: IReportTimeConfig): string {
        if (!(timeConfig.start && timeConfig.start.type)) {
            return '';
        }
        switch (timeConfig.start.type) {
            case TimeConfigType.TODAY: {
                return 'Today (when report is run)';
            }

            case TimeConfigType.CALENDAR_WEEKS_BEFORE: {
                return `${timeConfig.start.before ?? 0} calendar weeks after report is run`;
            }
            case TimeConfigType.MONTH: {
                return `${timeConfig.start.month} - ${timeConfig.start.year}`;
            }

            case TimeConfigType.WALMART_WEEK: {
                return `Walmart Week #${timeConfig.start.walmartWeek ?? 0}`;
            }
            case TimeConfigType.ASDA_WEEK: {
                return `Asda Week: Week ${timeConfig.start.asdaWeek} - ${timeConfig.start.year}`;
            }

            case TimeConfigType.CURRENT_WEEK: {
                return 'Current Week';
            }
            case TimeConfigType.ASDA_MONTH: {
                let monthVal = '',
                    yearVal = '';
                const monthArr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

                if (timeConfig.start.year && timeConfig.start.month) {
                    monthVal = monthArr[timeConfig.start.month - 1];
                    yearVal = timeConfig.start.year.toString().slice(-2);
                }
                return `ASDA Month: ${monthVal}-${yearVal}`;
            }
            case TimeConfigType.ASDA_YEAR: {
                return `ASDA Year: ${timeConfig.start.year} - full year `;
            }
            case TimeConfigType.ASDA_FISCAL_YEAR_TO_DATE: {
                return `ASDA Year: ${timeConfig.start.year} - fiscal year to date `;
            }
            case TimeConfigType.DATE: {
                return (
                    'Fixed Date: ' +
                    dayjs(timeConfig.start.date).format(DATE_FORMAT_DAYJS) +
                    ' - ' +
                    (dayjs(timeConfig.end?.date).isValid() ? dayjs(timeConfig.end?.date).format(DATE_FORMAT_DAYJS) : '')
                );
            }
            case TimeConfigType.LAST_WEEK: {
                return `Last X Weeks: ${timeConfig.start.before}`;
            }
            case TimeConfigType.LAST_YEAR_CURRENT_WEEK: {
                return `Previous Year Current Week: ${timeConfig.start.yearsBefore ?? 1} year${(timeConfig.start.yearsBefore ?? 1) > 1 ? 's' : ''} before`;
            }
            case TimeConfigType.LAST_YEAR_LAST_WEEK: {
                // eslint-disable-next-line max-len
                return `Previous Year Last X Weeks: ${timeConfig.start.before} week${(timeConfig.start.before ?? 1) > 1 ? 's' : ''} - ${timeConfig.start.yearsBefore} year${(timeConfig.start.yearsBefore ?? 1) > 1 ? 's' : ''} before`;
            }
            case TimeConfigType.LAST_YEAR_WEEKS_BEFORE: {
                // eslint-disable-next-line max-len
                return `Previous Year X Weeks Before: ${timeConfig.start.before} week${(timeConfig.start.before ?? 1) > 1 ? 's' : ''} - ${timeConfig.start.yearsBefore} year${(timeConfig.start.yearsBefore ?? 1) > 1 ? 's' : ''} before`;
            }
            case TimeConfigType.ASDA_WEEKS_BEFORE: {
                return `X Weeks Before: ${timeConfig.start.before}`;
            }
            default: {
                return 'Unknown time period type';
            }
        }
    }

    fetchStandardEffectiveLabel(timeConfigPoint: IReportTimeConfigPoint): Observable<string> {
        const today = dayjs();

        switch (timeConfigPoint.type) {
            case TimePeriodConfigType.asdaWeek: {
                if (isNil(timeConfigPoint.asdaWeek)) {
                    return of('');
                }

                return this.fetchAsdaWeekBefore(timeConfigPoint.asdaWeek ?? 0, ReportTimeConfigPointPosition.START);
            }

            default: {
                return of('Unknown time period type');
            }
        }
    }

    fetchAdvancedEffectiveLabel(
        timeConfigPoint: IReportTimeAdvancedConfigPoint,
        position: ReportTimeConfigPointPosition,
        timeType?: TemplateTimeType | null,
    ): Observable<string> {
        const today = dayjs();

        switch (timeConfigPoint.type) {
            case TimeConfigType.TODAY: {
                return of(today.format(DATE_FORMAT_DAYJS));
            }
            case TimeConfigType.DATE: {
                if (isNil(timeConfigPoint.date)) {
                    return of('');
                }
                return of(dayjs(timeConfigPoint.date).format(DATE_FORMAT_DAYJS));
            }
            case TimeConfigType.DAYS_BEFORE: {
                if (isNil(timeConfigPoint.before)) {
                    return of('');
                }
                return timeType === TemplateTimeType.FUTURE
                    ? of(
                          dayjs()
                              .add(timeConfigPoint.before ?? 0, 'days')
                              .format(DATE_FORMAT_DAYJS),
                      )
                    : of(
                          dayjs()
                              .subtract(timeConfigPoint.before ?? 0, 'days')
                              .format(DATE_FORMAT_DAYJS),
                      );
            }
            case TimeConfigType.CALENDAR_WEEKS_BEFORE: {
                if (isNil(timeConfigPoint.before)) {
                    return of('');
                }
                return timeType === TemplateTimeType.FUTURE
                    ? of(this.fetchCalendarWeekAfter(timeConfigPoint.before ?? 0, position))
                    : of(this.fetchCalendarWeekBefore(timeConfigPoint.before ?? 0, position));
            }
            case TimeConfigType.ASDA_WEEKS_BEFORE: {
                if (isNil(timeConfigPoint.before)) {
                    return of('');
                }
                return timeType === TemplateTimeType.FUTURE
                    ? this.fetchAsdaWeekAfter(timeConfigPoint.before ?? 0, position)
                    : this.fetchAsdaWeekBefore(timeConfigPoint.before ?? 0, position);
            }
            case TimeConfigType.MONTH: {
                if (isNil(timeConfigPoint.month)) {
                    return of('');
                }
                if (position === ReportTimeConfigPointPosition.START) {
                    return of(
                        `${dayjs()
                            .month(timeConfigPoint.month - 1)
                            .startOf('month')
                            .format(DATE_FORMAT_DAYJS)}`,
                    );
                } else if (position === ReportTimeConfigPointPosition.END) {
                    return of(
                        `${dayjs()
                            .month(timeConfigPoint.month - 1)
                            .endOf('month')
                            .format(DATE_FORMAT_DAYJS)}`,
                    );
                }
                return of('');
            }
            case TimeConfigType.MONTHS_BEFORE: {
                if (isNil(timeConfigPoint.before)) {
                    return of('');
                }
                const dateValue =
                    timeType === TemplateTimeType.FUTURE
                        ? today.add(timeConfigPoint.before ?? 0, 'months')
                        : today.subtract(timeConfigPoint.before ?? 0, 'months');
                if (position === ReportTimeConfigPointPosition.START) {
                    return of(`${dateValue.startOf('month').format(DATE_FORMAT_DAYJS)}`);
                } else if (position === ReportTimeConfigPointPosition.END) {
                    return of(`${dateValue.endOf('month').format(DATE_FORMAT_DAYJS)}`);
                }
                return of('');
            }
            case TimeConfigType.WALMART_WEEK: {
                if (isNil(timeConfigPoint.walmartWeek)) {
                    return of('');
                }
                return this.fetchWalmartWeek(+timeConfigPoint.walmartWeek, position);
            }
            case TimeConfigType.ASDA_WEEK: {
                if (isNil(timeConfigPoint.asdaWeek) || isNil(timeConfigPoint.yearsBefore)) {
                    return of('');
                }
                return timeType === TemplateTimeType.FUTURE
                    ? this.fetchAsdaWeekYearsAfter(timeConfigPoint.asdaWeek, timeConfigPoint.yearsBefore ?? 0, position)
                    : this.fetchAsdaWeekYearsBefore(timeConfigPoint.asdaWeek, timeConfigPoint.yearsBefore ?? 0, position);
            }
            case TimeConfigType.WHOLE_YEAR: {
                if (isNil(timeConfigPoint.yearType) || isNil(timeConfigPoint.yearsBefore)) {
                    return of('');
                }
                const dateValue =
                    timeType === TemplateTimeType.FUTURE
                        ? today.add(timeConfigPoint.yearsBefore ?? 0, 'years')
                        : today.subtract(timeConfigPoint.yearsBefore ?? 0, 'years');

                if (position === ReportTimeConfigPointPosition.START) {
                    if (timeConfigPoint.yearType === TimeConfigYearType.CALENDAR) {
                        return of(`${dateValue.startOf('year').format(DATE_FORMAT_DAYJS)}`);
                    } else if (timeConfigPoint.yearType === TimeConfigYearType.FISCAL) {
                        return of(`${dateValue.startOf('year').add(1, 'month').format(DATE_FORMAT_DAYJS)}`);
                    }
                } else if (position === ReportTimeConfigPointPosition.END) {
                    if (timeConfigPoint.yearType === TimeConfigYearType.CALENDAR) {
                        return of(`${dateValue.endOf('year').format(DATE_FORMAT_DAYJS)}`);
                    } else if (timeConfigPoint.yearType === TimeConfigYearType.FISCAL) {
                        return of(`${dateValue.endOf('year').add(1, 'month').format(DATE_FORMAT_DAYJS)}`);
                    }
                }
                return of('');
            }
            default: {
                return of('Unknown time period type');
            }
        }
    }

    protected isAsdaWeekYearExtended(year: number): Observable<boolean> {
        const LAST_ASDA_WEEK = 53;
        return this.apiService
            .fetchCalendar({
                asdaWeek: LAST_ASDA_WEEK,
                year: year,
            })
            .pipe(
                catchError(() => of([])),
                map((response) => !!response.length),
            );
    }

    protected fetchCalendarWeekBefore(before: number, position: ReportTimeConfigPointPosition): string {
        if (position === ReportTimeConfigPointPosition.START) {
            return dayjs().subtract(before, 'weeks').startOf('week').format(DATE_FORMAT_DAYJS);
        } else if (position === ReportTimeConfigPointPosition.END) {
            return dayjs().subtract(before, 'weeks').endOf('week').format(DATE_FORMAT_DAYJS);
        }
        return '';
    }

    protected fetchCalendarWeekAfter(after: number, position: ReportTimeConfigPointPosition): string {
        if (position === ReportTimeConfigPointPosition.START) {
            return dayjs().add(after, 'weeks').startOf('week').format(DATE_FORMAT_DAYJS);
        } else if (position === ReportTimeConfigPointPosition.END) {
            return dayjs().add(after, 'weeks').endOf('week').format(DATE_FORMAT_DAYJS);
        }
        return '';
    }

    protected fetchAsdaWeekBefore(before: number, position: ReportTimeConfigPointPosition): Observable<string> {
        return this.apiService
            .fetchCalendar({
                date: dayjs().subtract(before, 'weeks').format(API_DATE_FORMAT),
            })
            .pipe(
                catchError(() => of([])),
                map((response) => this.getMappedCalendarDate(response, position)),
            );
    }

    protected fetchAsdaWeekAfter(after: number, position: ReportTimeConfigPointPosition): Observable<string> {
        return this.apiService
            .fetchCalendar({
                date: dayjs().add(after, 'weeks').format(API_DATE_FORMAT),
            })
            .pipe(
                catchError(() => of([])),
                map((response) => this.getMappedCalendarDate(response, position)),
            );
    }

    protected fetchWalmartWeek(walmartWeek: number, position: ReportTimeConfigPointPosition): Observable<string> {
        return this.apiService
            .fetchCalendar({
                wmYrWk: walmartWeek,
            })
            .pipe(
                catchError(() => of([])),
                map((response) => this.getMappedCalendarDate(response, position)),
            );
    }

    protected fetchAsdaWeekYearsBefore(asdaWeek: number, yearsBefore: number, position: ReportTimeConfigPointPosition): Observable<string> {
        const year = dayjs()
            .subtract(yearsBefore ?? 0, 'years')
            .year();

        return this.apiService
            .fetchCalendar({
                asdaWeek: asdaWeek,
                year: year,
            })
            .pipe(
                catchError(() => of([])),
                map((response) => this.getMappedCalendarDate(response, position)),
            );
    }

    protected fetchAsdaWeekYearsAfter(asdaWeek: number, yearsBefore: number, position: ReportTimeConfigPointPosition): Observable<string> {
        const year = dayjs()
            .add(yearsBefore ?? 0, 'years')
            .year();

        return this.apiService
            .fetchCalendar({
                asdaWeek: asdaWeek,
                year: year,
            })
            .pipe(
                catchError(() => of([])),
                map((response) => this.getMappedCalendarDate(response, position)),
            );
    }

    protected getMappedCalendarDate(response: ICalendarData[], position: ReportTimeConfigPointPosition): string {
        if (!response.length) {
            return 'Invalid Week';
        }
        if (position === ReportTimeConfigPointPosition.START) {
            return `${dayjs(response[0].calendarDate).format(DATE_FORMAT_DAYJS)}`;
        } else if (position === ReportTimeConfigPointPosition.END) {
            return `${dayjs(response[response.length - 1].calendarDate).format(DATE_FORMAT_DAYJS)}`;
        }
        return '';
    }

    public mapToAdvancedConfig(timeConfig: IReportTimeStandardConfig): IReportTimeConfig | undefined {
        switch (timeConfig.timePeriod.type) {
            case TimePeriodConfigType.asdaMonth:
                const monthMap: { [key: string]: number } = {
                    Jan: 1,
                    Feb: 2,
                    Mar: 3,
                    Apr: 4,
                    May: 5,
                    Jun: 6,
                    Jul: 7,
                    Aug: 8,
                    Sep: 9,
                    Oct: 10,
                    Nov: 11,
                    Dec: 12,
                };
                const splitMonth = timeConfig.timePeriod.asdaMonth!.split('-');
                const year = splitMonth[1].length === 2 ? `20${splitMonth[1]}` : splitMonth[1];
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.ASDA_MONTH,
                        month: monthMap[timeConfig.timePeriod.asdaMonth!.split('-')[0]],
                        year: parseInt(year),
                    },
                    end: {
                        type: TimeConfigType.ASDA_MONTH,
                        month: monthMap[timeConfig.timePeriod.asdaMonth!.split('-')[0]],
                        year: parseInt(year),
                    },
                };
            case TimePeriodConfigType.asdaWeek:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.ASDA_WEEK,
                        asdaWeek: parseInt(timeConfig.timePeriod.asdaWeek!.toString().match(/Week\s(\d+)/)?.[1]!),
                        year: parseInt(timeConfig.timePeriod.asdaWeek!.toString().split('-')[1]),
                    },
                    end: {
                        type: TimeConfigType.ASDA_WEEK,
                        asdaWeek: parseInt(timeConfig.timePeriod.asdaWeek!.toString().match(/Week\s(\d+)/)?.[1]!),
                        year: parseInt(timeConfig.timePeriod.asdaWeek!.toString().split('-')[1]),
                    },
                };

            case TimePeriodConfigType.asdaYear:
                if (timeConfig.timePeriod.yearType === 'Fiscal year to date') {
                    return {
                        id: timeConfig.id,
                        name: timeConfig.name,
                        start: {
                            type: TimeConfigType.ASDA_FISCAL_YEAR_TO_DATE,
                            year: timeConfig.timePeriod.asdaYear,
                        },
                        end: {
                            type: TimeConfigType.ASDA_FISCAL_YEAR_TO_DATE,
                            year: timeConfig.timePeriod.asdaYear,
                        },
                    };
                } else {
                    return {
                        id: timeConfig.id,
                        name: timeConfig.name,
                        start: {
                            type: TimeConfigType.ASDA_YEAR,
                            year: timeConfig.timePeriod.asdaYear,
                        },
                        end: {
                            type: TimeConfigType.ASDA_YEAR,
                            year: timeConfig.timePeriod.asdaYear,
                        },
                    };
                }
            case TimePeriodConfigType.asdaYearFiscal:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.ASDA_FISCAL_YEAR_TO_DATE,
                        year: timeConfig.timePeriod.asdaFiscalYear,
                    },
                    end: {
                        type: TimeConfigType.ASDA_FISCAL_YEAR_TO_DATE,
                        year: timeConfig.timePeriod.asdaFiscalYear,
                    },
                };
            case TimePeriodConfigType.currentWeek:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.CURRENT_WEEK,
                    },
                    end: {
                        type: TimeConfigType.CURRENT_WEEK,
                    },
                };

            case TimePeriodConfigType.fixedDate:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.DATE,
                        date: timeConfig.timePeriod.from,
                    },
                    end: {
                        type: TimeConfigType.DATE,
                        date: timeConfig.timePeriod.to,
                    },
                };

            case TimePeriodConfigType.lastXWeeks:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.LAST_WEEK,
                        before: timeConfig.timePeriod.lastXWeeks,
                    },
                    end: {
                        type: TimeConfigType.LAST_WEEK,
                        before: timeConfig.timePeriod.lastXWeeks,
                    },
                };

            case TimePeriodConfigType.lastYearCurrentWeek:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.LAST_YEAR_CURRENT_WEEK,
                        yearsBefore: timeConfig.timePeriod.lastYearCurrentWeek,
                    },
                    end: {
                        type: TimeConfigType.LAST_YEAR_CURRENT_WEEK,
                        yearsBefore: timeConfig.timePeriod.lastYearCurrentWeek,
                    },
                };

            case TimePeriodConfigType.lastYearLastXWeeks:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.LAST_YEAR_LAST_WEEK,
                        before: timeConfig.timePeriod.before,
                        yearsBefore: timeConfig.timePeriod.yearsBefore,
                    },
                    end: {
                        type: TimeConfigType.LAST_YEAR_LAST_WEEK,
                        before: timeConfig.timePeriod.before,
                        yearsBefore: timeConfig.timePeriod.yearsBefore,
                    },
                };

            case TimePeriodConfigType.lastYearWeekXBefore:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.LAST_YEAR_WEEKS_BEFORE,
                        before: timeConfig.timePeriod.before,
                        yearsBefore: timeConfig.timePeriod.yearsBefore,
                    },
                    end: {
                        type: TimeConfigType.LAST_YEAR_WEEKS_BEFORE,
                        before: timeConfig.timePeriod.before,
                        yearsBefore: timeConfig.timePeriod.yearsBefore,
                    },
                };

            case TimePeriodConfigType.weekXBefore:
                return {
                    id: timeConfig.id,
                    name: timeConfig.name,
                    start: {
                        type: TimeConfigType.ASDA_WEEKS_BEFORE,
                        before: timeConfig.timePeriod.before,
                    },
                    end: {
                        type: TimeConfigType.ASDA_WEEKS_BEFORE,
                        before: timeConfig.timePeriod.before,
                    },
                };

            default:
                return undefined;
        }
        //return this.reportTimeConfig
    }
}
